import React, { useCallback, useRef, useMemo, useReducer, useState } from 'react';
import { Tooltip, Row, Col, Button, ConfigProvider, Table, Input } from '../../index';
import { ConfigProviderProps } from 'antd/es/config-provider';
import classNames from 'classnames';
import Highlighter from 'react-highlight-words';
import { TableProps, ColumnProps } from 'antd/lib/table';
import { TableRowSelection } from 'antd/lib/table/interface';
import { FilterFilled, SearchOutlined, CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import _ from 'lodash';
import DQueryForm, { IColumnsType } from '../d-query-form/DQueryForm';
import useDebounce from '../../hook/use-debounce';
import useDeepCompareEffect from '../../hook/use-deep-compare-effect';
import { useIntl } from '../../locale-provider';
import { pageSizeOptions, TSorterNames } from './config';

declare const ModeTypes: ['default', 'compact', string];
export declare type ModeType = typeof ModeTypes[number];

// 过滤，搜索不可以同时使用
export interface ITableColumnProps<T> extends ColumnProps<T> {
  commonFilter?: boolean; // 使用组件提供的过滤
  commonSorter?: boolean; // 使用组件提供的排序
  commonSearch?: boolean; // 使用组件提供的搜索
  searchRender?: (
    value: React.ReactText,
    row: T,
    index: number,
    highlightNode: React.ReactNode,
  ) => React.ReactNode; // （commonSearch 未开启时，无效）search 功能可以高亮被搜索的信息，但是会覆盖 render 方法。如果你希望保持search的所有功能，并自定义 render 方法，请使用 searchRender 代替。
}

interface IBasicTableProps<T> extends TableProps<T> {
  dataSource: any;
  columns: ITableColumnProps<T>[];
  dQueryFormColumns?: IColumnsType[];
  showQueryOptionBtns?: boolean;
  showQueryCollapseButton?: boolean;
  dQueryFormProps?: any;
  isQuerySearchOnChange?: boolean;
  rowSelection?: TableRowSelection<T>;
  loading?: boolean;
  queryMode?: ModeType;
  tableTitle?: string;
  hideContentBorder?: boolean;
  onReload?: () => void;
  showReloadBtn?: boolean;
  showFilter?: boolean;
  showSearch?: boolean;
  showBodyBg?: boolean;
  onSearch?: (query: string) => void;
  onSearchIcon?: () => void;
  searchPlaceholder?: string;
  searchPos?: 'full' | 'right' | string;
  filterType?: 'line' | 'half' | 'none' | string;
  reloadBtnPos?: 'left' | 'right' | string;
  reloadBtnType?: 'icon' | 'btn' | string;
  leftHeader?: React.ReactNode | string | number;
  customHeader?: React.ReactNode | string | number;
  antProps?: TableProps<T>;
  antConfig?: {} & ConfigProviderProps;
}

type TActionName = 'search' | 'filter';

interface IColumnsReducerValue {
  title: string;
  dataIndex: string;
  type?: TActionName;
  value?: any;
}
interface IColumnsReducerState {
  [x: string]: IColumnsReducerValue;
}

type TColumnsReducerAction =
  | {
    type: 'update';
    dataIndex: string | number;
    value?: any;
    updateType?: TActionName;
  }
  | { type: 'clear'; initialState: any };

type TSorterReducerAction = { type: 'update'; value: any } | { type: 'clear' };

function sorterReducer(state: any, action: TSorterReducerAction) {
  switch (action.type) {
    case 'update':
      return {
        ...action.value,
      };
    case 'clear':
      return {};
    default:
      return state;
  }
}

// 泛型函数组件 https://juejin.im/post/5cd7f2c4e51d453a7d63b715#heading-7
function BasicTable<T>(props: IBasicTableProps<T>) {
  const prefixCls = `${props.prefixCls || 'dantd'}-table`;
  const filterType = props.filterType || 'half';
  const tmpDataSource = props.dataSource || ([] as T[]);
  const { t } = useIntl();
  const [dataSource, setDataSource] = useState(tmpDataSource);
  const [dQueryFormValues, setDQueryFormValues] = useState<any>({});
  const [isQueryInit, setQueryInit] = useState(false);
  const {
    showFilter = true,
    showSearch = true,
    showReloadBtn = true,
    showQueryOptionBtns = true,
    showQueryCollapseButton = true,
    dQueryFormProps = {},
    isQuerySearchOnChange = true,
    loading = false,
    showBodyBg = false,
    dQueryFormColumns,
    queryMode = 'default',
    searchPos = 'full',
    reloadBtnPos = 'right',
    reloadBtnType = 'icon',
  } = props;
  // 整理搜索区的展示状态
  // 是否展示长条搜索区
  const showFullSearch = showSearch && searchPos === 'full';
  // 搜索按钮展示的位置
  const showReloadBtn2SearchRight =
    searchPos === 'full' && showReloadBtn && reloadBtnPos === 'right';
  const showReloadBtn2FilterRight =
    (!showSearch || searchPos !== 'full') && showReloadBtn && reloadBtnPos === 'right';
  const showReloadBtn2FilterLeft = showReloadBtn && reloadBtnPos === 'left';

  const searchPlaceholder = props.searchPlaceholder || t('table.search.placeholder');
  const tableClassName = classNames(prefixCls, props.className);
  const tableContentClassName = classNames({
    [`${prefixCls}-table-content`]: true,
    [`${prefixCls}-table-content-noborder`]: props.hideContentBorder,
  });
  const tableBodyCls = classNames({
    [`${prefixCls}-body`]: !!dQueryFormColumns || showBodyBg,
    [`${prefixCls}-body-compact`]: queryMode === 'compact',
  });

  const tableQueryCls = classNames({
    [`${prefixCls}-query`]: !!dQueryFormColumns,
    [`${prefixCls}-query-compact`]: queryMode === 'compact',
  });
  const filterSearchInputRef = useRef({}) as any;
  const clearFiltersRef = useRef({}) as any;
  const [searchQuery, setSearchQuery] = useState('');
  const [showRightHeader, setShowRightHeader] = useState<boolean>(false);
  const [sorterState, sorterDispatch] = useReducer(sorterReducer, {});
  const rowSelection = props.rowSelection;

  const selectRowNum = rowSelection
    ? rowSelection.selectedRowKeys && rowSelection.selectedRowKeys.length
    : -1;
  const sorterNames = {
    ascend: t('table.sort.ascend'),
    descend: t('table.sort.descend'),
  };

  const showTotal = (total: number) => {
    return `${t('table.total.prefix')} ${total} ${t('table.total.suffix')}`;
  };

  const columnsDataIndexArr = props.columns.map((columnItem) => columnItem.dataIndex as string) || [];
  const dataSourceMap = useMemo(() => {
    if (!props.dataSource || !Array.isArray(props.dataSource)) {
      return [];
    }
    return props.dataSource.reduce((acc, curVal, curIdx) => {
      let curTarget = '';

      Object.entries(curVal).forEach(([curKey, curItem]) => {
        if (
          columnsDataIndexArr.indexOf(curKey) >= 0 &&
          (typeof curItem === 'string' || typeof curItem === 'number')
        ) {
          curTarget = `${curTarget} ${curItem}`;
        }
      });

      return {
        ...acc,
        [curIdx]: curTarget.toLowerCase(),
      };
    }, {});
  }, [columnsDataIndexArr, props.dataSource]);

  const columnsMap = useMemo(() => {
    const result = {} as any;
    if (props.columns && props.columns.length > 0) {
      props.columns.forEach((columnItem) => {
        if (columnItem.dataIndex) {
          result[columnItem.dataIndex as string] = {
            title: columnItem.title,
            dataIndex: columnItem.dataIndex,
            value: null,
            commonSearch: columnItem.commonSearch,
            commonFilter: columnItem.commonFilter,
            commonSorter: columnItem.commonSorter,
          };
        }
      });
    }
    return result;
  }, [props.columns]);

  function columnInit(initColumnState) {
    return initColumnState;
  }

  function columnsReducer(state: IColumnsReducerState, action: TColumnsReducerAction) {
    switch (action.type) {
      case 'update':
        return {
          ...state,
          [action.dataIndex]: {
            ...state[action.dataIndex],
            dataIndex: action.dataIndex,
            type: action.updateType,
            value: action.value,
          },
        };
      case 'clear':
        return columnInit(action.initialState);
      default:
        return state;
    }
  }

  const [columnsState, columnsDispatch] = useReducer(columnsReducer, columnsMap, columnInit);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useDeepCompareEffect(() => {
    // 模糊匹配
    let newDataSource = [...tmpDataSource];
    if (showSearch && debouncedSearchQuery && debouncedSearchQuery.trim()) {
      if (props.onSearch) {
        // 使用用户自定义的search回调
        props.onSearch(debouncedSearchQuery.trim());
        return;
      } else {
        // 使用组件默认的search回调

        const debouncedSearchArr = debouncedSearchQuery.trim().toLowerCase().split(' ');
        newDataSource = newDataSource.filter((fiterItem, filterIdx) => {
          const filterStr = dataSourceMap[filterIdx];
          return debouncedSearchArr.every((someItem) => filterStr.indexOf(someItem) >= 0);
        });
      }
    }

    if (dQueryFormColumns && Object.keys(dQueryFormValues).length > 0) {
      newDataSource = _.filter(newDataSource, (sourceItem) => {
        return Object.entries(dQueryFormValues).every(([queryKey, queryValue]) => {
          if (!queryValue || (Array.isArray(queryValue) && queryValue.length === 0)) {
            return true;
          }
          if (typeof queryValue === 'string') {
            return sourceItem[queryKey] && sourceItem[queryKey].indexOf(queryValue) >= 0;
          }
          if (Array.isArray(queryValue) && queryValue.length > 0) {
            return queryValue.indexOf(sourceItem[queryKey]) >= 0;
          }
        });
      });
    }

    setDataSource(newDataSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, dQueryFormValues, props.dataSource]);

  const handleChange = (pagination: any, filters: any, sorter: any, extra: any) => {
    sorterDispatch({
      type: 'update',
      value: sorter,
    });

    Object.entries(filters).forEach(([filterKey, filterValue]) => {
      if (columnsMap[filterKey].commonFilter) {
        columnsDispatch({
          type: 'update',
          dataIndex: filterKey,
          updateType: 'filter',
          value: filterValue,
        });
      }
    });

    checkRightHeader(filters, sorter);

    if (props.onChange) {
      props.onChange(pagination, filters, sorter, extra);
    }
  };

  const checkRightHeader = (filters?: any, sorter?: any, search?: any) => {
    const checkSorter = sorter || sorterState;
    const checkFilters = filters || columnsState;
    const checkSearch = search || columnsState;
    const isSearch = Object.values(checkSearch).some((columnItem: any) => {
      return !!(columnItem.type === 'search' && columnItem.value);
    });
    let isFilter = false;
    if (filters) {
      isFilter = Object.values(checkFilters).some((columnItem: any) => {
        return columnItem && columnItem.length > 0;
      });
    } else {
      isFilter = Object.values(checkFilters).some((columnItem: any) => {
        return !!(columnItem.type === 'filter' && columnItem.value && columnItem.value.length > 0);
      });
    }
    const isSorter = !!checkSorter.column;

    const res = isSearch || isFilter || isSorter;
    setShowRightHeader(res);
  };

  const handleFilterSearch = useCallback(
    (
      selectedKeys: React.ReactText[] | undefined,
      confirm: (() => void) | undefined,
      dataIndex: string | number,
    ) => {
      if (confirm) {
        confirm();
      }
      if (selectedKeys && dataIndex) {
        columnsDispatch({
          type: 'update',
          dataIndex,
          updateType: 'search',
          value: selectedKeys[0],
        });
      }
    },
    [],
  );

  const handleFilterSearchReset = useCallback(
    (
      clearFilters: ((selectedKeys: string[]) => void) | undefined,
      dataIndex: string | number | undefined,
    ) => {
      if (clearFilters) {
        clearFilters([]);
      }
      if (dataIndex) {
        columnsDispatch({
          type: 'update',
          dataIndex,
        });
      }
    },
    [],
  );

  const handleClearAll = () => {
    sorterDispatch({ type: 'clear' });
    columnsDispatch({ type: 'clear', initialState: columnsMap });
    setShowRightHeader(false);
    setTimeout(() => {
      Object.values(clearFiltersRef.current).forEach((filterItem: any) => {
        if (filterItem && filterItem.clearFilters) {
          filterItem.clearFilters([]);
        }
      });
    });
  };

  const handleSortClear = () => {
    sorterDispatch({ type: 'clear' });
    checkRightHeader(null, {});
  };

  const handleFilterClear = (columnValue: IColumnsReducerValue) => {
    columnsDispatch({
      type: 'update',
      dataIndex: columnValue.dataIndex,
      value: [],
    });

    const tmpFilters = Object.values(columnsState).map((filterItem: any) => {
      if (filterItem.dataIndex === columnValue.dataIndex) {
        return {
          [filterItem.dataIndex]: [],
        };
      }
      return {
        [filterItem.dataIndex]: filterItem.value || [],
      };
    });

    checkRightHeader(tmpFilters, sorterState, columnsState);
  };

  const handleFilterSearchClear = (columnValue: IColumnsReducerValue) => {
    columnsDispatch({
      type: 'update',
      dataIndex: columnValue.dataIndex,
    });

    if (
      clearFiltersRef.current[columnValue.dataIndex] &&
      clearFiltersRef.current[columnValue.dataIndex].clearFilters
    ) {
      clearFiltersRef.current[columnValue.dataIndex].clearFilters([]);
    }

    checkRightHeader(null, sorterState, {
      ...columnsState,
      [columnValue.dataIndex]: {
        title: columnsState[columnValue.dataIndex].title,
        dataIndex: columnsState[columnValue.dataIndex].dataIndex,
        value: null,
      },
    });
  };
  const renderColumns: () => ITableColumnProps<Object>[] = () => {
    // if (!dataSource || (dataSource && dataSource.length === 0)) {
    //   return props.columns;
    // }

    return props.columns.map((columnItem) => {
      const currentItem = _.cloneDeep(columnItem);

      // filter
      if (currentItem.commonFilter && !currentItem.filters) {
        const filters = _.uniq(_.map(dataSource, columnItem.dataIndex as string));
        currentItem.filters = filters.map((value: string) => {
          return {
            text: value,
            value,
          };
        });

        currentItem.filterIcon = () => <FilterFilled />;
        currentItem.filteredValue = columnsState[columnItem.dataIndex as string].value;
        currentItem.onFilter = (value, record: any) => {
          if (currentItem.dataIndex && record[currentItem.dataIndex as string]) {
            return record[currentItem.dataIndex as string] === value;
          }

          return false;
        };
      }

      // sort
      if (currentItem.commonSorter) {
        currentItem.sorter = (aItem: any, bItem: any) => {
          const a = aItem[currentItem.dataIndex as string];
          const b = bItem[currentItem.dataIndex as string];
          // number
          const numA = Number(a);
          const numB = Number(b);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }

          // date
          const dateA = +new Date(a);
          const dateB = +new Date(b);
          if (!isNaN(dateA) && !isNaN(dateB)) {
            return dateA - dateB;
          }

          // string
          if (typeof a === 'string' && typeof b === 'string') {
            return a > b ? 1 : -1;
          }

          return 0;
        };
        currentItem.sortOrder =
          sorterState.columnKey === currentItem.dataIndex && sorterState.order;
      }

      // Search
      if (currentItem.commonSearch) {
        currentItem.filterIcon = () => <SearchOutlined />;

        currentItem.onFilterDropdownVisibleChange = (visible: boolean) => {
          if (visible && filterSearchInputRef.current && filterSearchInputRef.current.select) {
            setTimeout(() => {
              if (filterSearchInputRef.current && filterSearchInputRef.current.select) {
                filterSearchInputRef.current.select();
              }
              return null;
            });
          }
        };

        currentItem.filterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          clearFiltersRef.current[currentItem.dataIndex as string] = {
            clearFilters,
          };

          return (
            <div style={{ padding: 8 }}>
              <Input
                data-testid="filter-search-input"
                autoFocus={true}
                ref={(node) => {
                  filterSearchInputRef.current = node;
                }}
                placeholder={t('table.filter.search.placeholder')}
                value={selectedKeys && selectedKeys[0]}
                onChange={(e) => {
                  if (setSelectedKeys) {
                    return setSelectedKeys(e.target.value ? [e.target.value] : []);
                  }
                  return [];
                }}
                onPressEnter={() =>
                  handleFilterSearch(selectedKeys, confirm, currentItem.dataIndex as string)
                }
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Button
                type="primary"
                onClick={() =>
                  handleFilterSearch(selectedKeys, confirm, currentItem.dataIndex as string)
                }
                icon="search"
                size="small"
                data-testid="search-btn-ok"
                style={{ width: 90, marginRight: 8 }}
              >
                {t('table.filter.search.btn.ok')}
              </Button>
              <Button
                onClick={() => handleFilterSearchReset(clearFilters, currentItem.dataIndex as string)}
                size="small"
                style={{ width: 90 }}
              >
                {t('table.filter.search.btn.cancel')}
              </Button>
            </div>
          );
        };

        let searchWords: any[] = [];

        const tmpStateValue = columnsState[currentItem.dataIndex as string].value;
        if (typeof tmpStateValue === 'string') {
          searchWords = [tmpStateValue];
        }

        if (Array.isArray(tmpStateValue) && typeof tmpStateValue[0] === 'string') {
          searchWords = [tmpStateValue[0]];
        }

        currentItem.onFilter = (value: any, record: any) => {
          return record[currentItem.dataIndex as string]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        };

        if (!currentItem.render) {
          currentItem.render = (value, row, index) => {
            if (currentItem.searchRender) {
              return currentItem.searchRender(
                value,
                row,
                index,
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                  searchWords={searchWords}
                  autoEscape
                  textToHighlight={String(value)}
                />,
              );
            } else {
              return (
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                  searchWords={searchWords}
                  autoEscape
                  textToHighlight={String(value)}
                />
              );
            }
          };
        }
      }
      return currentItem;
    });
  };

  const isSearch = Object.values(columnsState).some((columnItem: any) => {
    return !!(columnItem.type === 'search' && columnItem.value);
  });

  const isFilter = Object.values(columnsState).some((columnItem: any) => {
    return !!(columnItem.type === 'filter' && columnItem.value && columnItem.value.length > 0);
  });

  const handleReload = () => {
    if (props.onReload) {
      props.onReload();
    }
  };
  const handleSearchIcon = () => {
    if (props.onSearch) {
      props.onSearch(debouncedSearchQuery.trim());
    } else if (props.onSearchIcon) {
      props.onSearchIcon()
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  const handleDQueryFormInit = (data) => {
    if (!isQueryInit) {
      setDQueryFormValues(data);
      setQueryInit(true);
    }
  };

  const handleDQueryFormChange = (data) => {
    setDQueryFormValues(data);
  };

  const handleDQueryFormReset = (data) => {
    setDQueryFormValues(data);
  };

  const renderRightHeader = (params) => {
    if (!showFilter) {
      return null;
    }
    return (
      <>
        <div>
          <b
            style={{
              display: 'inline-block',
              marginTop: 3,
            }}
          >
            {t('table.filter.header.title')}
          </b>
        </div>
        {(isSearch || isFilter) &&
          Object.values(columnsState as IColumnsReducerState).map((columnValue) => {
            if (columnValue.type === 'search' && columnValue.value) {
              return (
                <div
                  key={`search-header-${columnValue.dataIndex}`}
                  className={`${params.headerClsPrefix}-item`}
                >
                  <Tooltip
                    title={
                      <span>
                        {columnValue.title}
                        {':'}&nbsp;
                        {columnValue.value}
                      </span>
                    }
                  >
                    <Button
                      size="small"
                      className="table-header-item-btn"
                      onClick={() => handleFilterSearchClear(columnValue)}
                    >
                      <span className="table-header-item-btn-content">
                        {columnValue.title}
                        {':'}&nbsp;
                        {columnValue.value}
                      </span>
                      <CloseOutlined />
                    </Button>
                  </Tooltip>
                </div>
              );
            }

            if (
              columnValue.type === 'filter' &&
              columnValue.value &&
              columnValue.value.length > 0
            ) {
              return (
                <div
                  key={`search-header-${columnValue.dataIndex}`}
                  className={`${params.headerClsPrefix}-item`}
                >
                  <Tooltip
                    title={
                      <span>
                        {columnValue.title}
                        {':'}&nbsp;
                        {columnValue.value.join(',')}
                      </span>
                    }
                  >
                    <Button
                      size="small"
                      className="table-header-item-btn"
                      onClick={() => handleFilterClear(columnValue)}
                    >
                      <span className="table-header-item-btn-content">
                        {columnValue.title}
                        {':'}&nbsp;
                        {columnValue.value.join(',')}
                      </span>
                      <CloseOutlined />
                    </Button>
                  </Tooltip>
                </div>
              );
            }
            return null;
          })}
        {sorterState.columnKey && sorterState.column && (
          <div className={`${params.headerClsPrefix}-item`}>
            <Tooltip
              title={
                <span>
                  {sorterState.column.title}
                  {`: ${sorterNames[sorterState.order as TSorterNames]}`}
                </span>
              }
            >
              <Button size="small" className="table-header-item-btn" onClick={handleSortClear}>
                <span className="table-header-item-btn-content">
                  {sorterState.column.title}
                  {`: ${sorterNames[sorterState.order as TSorterNames]}`}
                </span>
                <CloseOutlined />
              </Button>
            </Tooltip>
          </div>
        )}
        <div className={`${params.headerClsPrefix}-item`}>
          <Button size="small" type="link" data-testid="btn-clearall" onClick={handleClearAll}>
            {t('table.filter.header.btn.clear')}
          </Button>
        </div>
      </>
    );
  };

  const renderSearch = () => {
    return (
      <Tooltip placement="topLeft" title={searchPlaceholder}>
        <Input
          data-testid="search-input"
          prefix={
            reloadBtnPos === 'right' && <SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} onClick={handleSearchIcon} />
          }
          suffix={
            reloadBtnPos === 'left' && <SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} onClick={handleSearchIcon} />
          }
          allowClear={true}
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
        />
      </Tooltip>
    );
  };

  const renderReloadBtn = () => {
    if (reloadBtnType === 'icon') {
      const reloadBtnCls = classNames({
        [`${prefixCls}-header-loadbtn`]: true,
        [`${prefixCls}-header-loadbtn-icon`]: true,
        [`${prefixCls}-header-loadbtn-right`]: reloadBtnPos === 'right',
        [`${prefixCls}-header-loadbtn-left`]: reloadBtnPos === 'left',
      });
      return (
        <ReloadOutlined
          data-testid="reload-btn"
          onClick={handleReload}
          spin={loading}
          className={reloadBtnCls}
        />
      );
    }

    if (reloadBtnType === 'btn') {
      const reloadBtnCls = classNames({
        [`${prefixCls}-header-loadbtn`]: true,
        [`${prefixCls}-header-loadbtn-btn`]: true,
        [`${prefixCls}-header-loadbtn-right`]: reloadBtnPos === 'right',
        [`${prefixCls}-header-loadbtn-left`]: reloadBtnPos === 'left',
      });
      return (
        <Button
          className={reloadBtnCls}
          loading={loading}
          icon={<ReloadOutlined />}
          data-testid="reload-btn"
          onClick={handleReload}
        />
      );
    }
  };

  return (
    <ConfigProvider {...props.antConfig}>
      <div className={tableClassName} style={props.style}>
        {dQueryFormColumns && (
          <div className={tableQueryCls}>
            <DQueryForm
              onChange={isQuerySearchOnChange ? handleDQueryFormChange : handleDQueryFormInit}
              onReset={handleDQueryFormReset}
              onSearch={handleDQueryFormChange}
              columns={dQueryFormColumns}
              showOptionBtns={showQueryOptionBtns}
              showCollapseButton={showQueryCollapseButton}
              {...dQueryFormProps}
            />
          </div>
        )}
        <div className={tableBodyCls}>
          {!!props.tableTitle && <h3> {props.tableTitle} </h3>}
          {props.customHeader && (
            <div data-testid="custom-header" className={`${prefixCls}-header-custom`}>
              {props.customHeader}
            </div>
          )}
          {showFullSearch && (
            <Row className={`${prefixCls}-header-search`}>
              {showSearch ? renderSearch() : <div></div>}
              {showReloadBtn2SearchRight && renderReloadBtn()}
            </Row>
          )}

          {filterType === 'none' && searchPos !== 'right' && (
            <Row className={`${prefixCls}-header-filter`}>
              {showReloadBtn2FilterLeft && renderReloadBtn()}
              <Col
                data-testid="left-header"
                className={classNames(
                  `${prefixCls}-header-filter-left`,
                  props.leftHeader !== undefined && `${prefixCls}-header-filter-left-minh`,
                )}
              >
                {props.leftHeader}
              </Col>
              {showReloadBtn2FilterRight && renderReloadBtn()}
            </Row>
          )}
          {filterType === 'none' && showSearch && searchPos === 'right' && (
            <Row className={`${prefixCls}-header-filter`}>
              {showReloadBtn2FilterLeft && renderReloadBtn()}
              <Col
                data-testid="left-header"
                className={classNames(
                  `${prefixCls}-header-filter-left`,
                  props.leftHeader !== undefined && `${prefixCls}-header-filter-left-minh`,
                )}
                span={12}
              >
                {props.leftHeader}
              </Col>
              <Col
                data-testid="right-header"
                className={`${prefixCls}-header-filter-right`}
                span={12}
              >
                {renderSearch()}
              </Col>
              {showReloadBtn2FilterRight && renderReloadBtn()}
            </Row>
          )}
          {filterType === 'line' && (
            <Row className={`${prefixCls}-header-filter`}>
              {showReloadBtn2FilterLeft && renderReloadBtn()}
              <Col
                data-testid="right-header"
                className={`${prefixCls}-header-filter-line`}
                span={24}
              >
                {showRightHeader &&
                  renderRightHeader({
                    headerClsPrefix: `${prefixCls}-header-filter-line`,
                  })}
              </Col>
              {showReloadBtn2FilterRight && renderReloadBtn()}
            </Row>
          )}
          {filterType === 'half' && (
            <Row className={`${prefixCls}-header-filter`}>
              {showReloadBtn2FilterLeft && renderReloadBtn()}
              <Col
                data-testid="left-header"
                className={classNames(
                  `${prefixCls}-header-filter-left`,
                  props.leftHeader !== undefined && `${prefixCls}-header-filter-left-minh`,
                )}
                span={12}
              >
                {props.leftHeader}
              </Col>
              <Col
                data-testid="right-header"
                className={`${prefixCls}-header-filter-right`}
                span={12}
              >
                {showRightHeader &&
                  renderRightHeader({
                    headerClsPrefix: `${prefixCls}-header-filter-right`,
                  })}
              </Col>
              {showReloadBtn2FilterRight && renderReloadBtn()}
            </Row>
          )}
          <Row className={`${prefixCls}-table-wrapper`}>
            <div className={tableContentClassName}>
              <Table
                loading={loading}
                columns={renderColumns()}
                dataSource={dataSource}
                bordered={false}
                rowSelection={rowSelection}
                onChange={handleChange}
                pagination={{
                  size: 'small',
                  showTotal: showTotal,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: pageSizeOptions,
                  ...props.pagination,
                }}
                {...props.antProps}
              />
              {rowSelection && selectRowNum !== undefined && selectRowNum > 0 && (
                <div className={`${prefixCls}-table-content-select-num`}>
                  {t('table.select.num')}
                  {selectRowNum}
                </div>
              )}
            </div>
          </Row>
        </div>
      </div>
    </ConfigProvider>
  );
}

export default BasicTable;
